import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";

/**
 * Recording a fix on an incident that has **no fix plan**.
 *
 * This is a different act from the per-problem fix log, and the difference is
 * not cosmetic. A fix log names a problem on the plan and resolves that entry;
 * when the last one resolves, the incident reaches fix-applied by itself. With
 * no plan there is no enumerated list of problems, so "no more problems" is not
 * computable — and the fix log degrades to a plain note that moves nothing.
 *
 * The route the spec always named for this case is the hand-set transition,
 * which IS the recorded manual setup check: who verified, when, and what they
 * did. It is the same destination by a different road, so it carries the same
 * consequences — the trusted data start date resets and a real booking is what
 * proves it — and it is worded to say so before it is used.
 */
export function HandFixModal({
  onSubmit,
  onClose,
  pending,
}: {
  onSubmit: (note: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [note, setNote] = useState("");

  return (
    <ActionModal
      title="Record a fix made by hand"
      submitLabel="Record the fix"
      onSubmit={() => onSubmit(note.trim())}
      onClose={onClose}
      pending={pending}
      disabled={!note.trim()}
    >
      <div>
        <label className={FIELD_LABEL_CLASS}>
          What was done, and who verified it
        </label>
        <textarea
          className={`${INPUT_CLASS} min-h-[88px]`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Added the booking domain to the Conversion Linker by hand and published; verified the _gl parameter survives the hop"
          autoFocus
        />
      </div>
      <p className="text-xs text-gray-400">
        Nothing on this incident is diagnosed, so there is no problem to log
        this against — this records your own check instead, which is what the
        spec has always called for where no automated check exists or can run.
        The hotel moves to Monitoring, its trusted data start date resets, and a
        real ad-driven booking after that is what closes the incident.
        <span className="block mt-1">
          Write what you actually changed. This sentence is the blocked-on note
          the next person reads, and the record of who verified it.
        </span>
      </p>
    </ActionModal>
  );
}
